import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';

// Helper function to get AppBar height
const getAppBarHeight = () => 64;

// TeamSection component
function TeamMembersSection({ teamMembers }) {
  if (!teamMembers || teamMembers.length === 0) return null;

  return (
    <Box sx={{ paddingY: 6 }}>
      <Box sx={{ textAlign: 'center', marginBottom: 8 }}>
        <Typography 
          variant="h2" 
          component="h2" 
          sx={{ 
            fontWeight: 800,
            background: 'linear-gradient(135deg, #2561b3 0%, #1e40af 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            marginBottom: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}
        >
          Meet Our Team
        </Typography>
        <Box sx={{ 
          width: 80, 
          height: 4, 
          background: 'linear-gradient(90deg, #2561b3, #60a5fa)',
          margin: '0 auto',
          borderRadius: 2
        }} />
      </Box>
      
      <Grid container spacing={4} justifyContent="center">
        {teamMembers.map((member) => (
          <Grid item key={member.name} xs={12} sm={6} md={4}>
            <Box sx={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: 3,
              padding: 4,
              textAlign: 'center',
              height: '100%',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(37, 97, 179, 0.15)'
              }
            }}>
              {member.image && (
                <Box 
                  component="img"
                  src={member.image} 
                  alt={member.name} 
                  sx={{
                    width: 120, 
                    height: 120, 
                    borderRadius: '50%', 
                    objectFit: 'cover', 
                    marginBottom: 3,
                    border: '4px solid #ffffff',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                  }}
                />
              )}
              <Typography variant="h5" component="h3" sx={{ 
                fontWeight: 700, 
                marginBottom: 1,
                color: '#1e293b'
              }}>
                {member.name}
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#2561b3', 
                marginBottom: 2,
                fontWeight: 600
              }}>
                {member.role}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#64748b', 
                lineHeight: 1.6,
                fontSize: '0.95rem'
              }}>
                {member.bio}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      
      <Typography variant="h6" sx={{ 
        marginTop: 15, 
        marginBottom: 2,
        textAlign: 'center',
        fontStyle: 'italic', 
        color: '#64748b',
        maxWidth: '600px',
        marginX: 'auto'
      }}>
        We collaborate with a distinguished network of leading experts and academics to deliver specialized solutions.
      </Typography>
    </Box>
  );
}

// CollaboratorsSection component
function CollaboratorsSection({ collaborators }) {
  if (!collaborators || collaborators.length === 0) return null;

  return (
    <Box sx={{ paddingY: 0, marginTop: 0 }}>
      <Typography variant="h4" component="h3" sx={{ 
        fontWeight: 700,
        marginBottom: 6, 
        textAlign: 'center', 
        color: '#1e293b',
        position: 'relative'
      }}>
        Key Collaborators
        <Box sx={{
          position: 'absolute',
          bottom: -10,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 60,
          height: 3,
          backgroundColor: '#2561b3',
          borderRadius: 2
        }} />
      </Typography>
      
      <Box sx={{ maxWidth: '1000px', marginX: 'auto' }}>
        {collaborators.map((collaborator, index) => (
          <Box 
            key={collaborator.name} 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'center' }, 
              gap: { xs: 3, md: 4 }, 
              marginBottom: index === collaborators.length - 1 ? 0 : 4,
              padding: 4,
              borderRadius: 3,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 30px rgba(37, 97, 179, 0.12)'
              }
            }}
          >
            {/* Circular image */}
            {collaborator.image && (
              <Box 
                component="img"
                src={collaborator.image} 
                alt={collaborator.name} 
                sx={{
                  width: { xs: 120, md: 100 }, 
                  height: { xs: 120, md: 100 }, 
                  borderRadius: '50%', 
                  objectFit: 'cover',
                  flexShrink: 0,
                  border: '4px solid #ffffff',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)'
                }}
              />
            )}
            {/* Text content */}
            <Box sx={{ 
              flex: 1,
              textAlign: { xs: 'center', md: 'left' }
            }}>
              <Typography variant="h5" component="h4" sx={{ 
                fontWeight: 700, 
                marginBottom: 1,
                color: '#1e293b'
              }}>
                {collaborator.name}
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#2561b3', 
                marginBottom: 2, 
                fontWeight: 600
              }}>
                {collaborator.role}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: '#64748b', 
                lineHeight: 1.7,
                fontSize: '1rem'
              }}>
                {collaborator.bio}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function TeamSection({ teamData }) {
  const appBarHeight = getAppBarHeight();

  return (
    <Box id="team-section" className="snap-section" sx={{ 
      position: 'relative',
      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)', 
      paddingTop: `${appBarHeight}px`,
      paddingY: 10,
      overflow: 'hidden'
    }}>
      {/* Subtle background pattern */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        backgroundImage: `radial-gradient(circle at 25px 25px, #2561b3 2px, transparent 0), radial-gradient(circle at 75px 75px, #2561b3 2px, transparent 0)`,
        backgroundSize: '100px 100px',
        backgroundPosition: '0 0, 50px 50px'
      }} />
      
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <TeamMembersSection teamMembers={teamData.team} />
        <CollaboratorsSection collaborators={teamData.collaborators} />
      </Container>
    </Box>
  );
} 